import './Role.scss';
import Loader from 'components/atoms/Loader/Loader';
import Notification from 'components/atoms/Notification/Notification';
import AppBar from 'components/organisms/AppBar/AppBar';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import { appSwitchRoutes } from 'constants/routes';
import { candidateEmail } from 'constants/candidateEmail';
import { useMyRoleQuery } from 'generated/graphql';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  NavLink,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import RoleStageCard from './RoleStageCard/RoleStageCard';

const Role: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();
  const { roleId } = useParams();

  const myRoleQueryResponse = useMyRoleQuery({
    variables: { id: roleId as string },
    skip: !roleId,
    onError: error => {
      handleMsgType({
        type: TNotification.error,
        message: error?.message,
      });
    },
    fetchPolicy: 'network-only',
  });

  const role = myRoleQueryResponse.data?.myRole;

  // Mirror of the CandidatePortal guard: a portal-enabled role has no legacy
  // stage list — any stale link into it lands on the portal instead.
  if (role?.candidatePortalEnabled) {
    return (
      <Navigate
        to={appSwitchRoutes.candidatePortal.getUrl(role.id)}
        replace
      />
    );
  }

  return (
    <>
      <HelmetAndPageAnnouncer pageTitle={role?.name || t('roles.roleDetails')} />
      <div className="Role" aria-live="polite">
        <AppBar />
        <nav className="Role__Nav">
          <NavLink
            to={appSwitchRoutes.roles.getUrl()}
            onClick={e => {
              e.preventDefault();
              navigate(appSwitchRoutes.roles.getUrl());
            }}
            aria-label={t('roles.backToRoleList')}
            direction="backward"
            className="Role__Nav__BackButton"
          >
            {t('roles.roleList')}
          </NavLink>
        </nav>
        <main>
          {myRoleQueryResponse.loading ? (
            <Loader isOverlay />
          ) : myRoleQueryResponse.error || !role ? (
            <div className="Role__NotificationWrapper">
              <Notification
                notification={{
                  icon: 'Warning',
                  color: 'Purple',
                  message: t(
                    'roles.unfortunatelyThereHasBeenProblemLoadingRoles',
                    {
                      candidateEmail,
                    }
                  ),
                }}
              />
            </div>
          ) : (
            <>
              <div className="Role__Header">
                {role.company.logoUrl ? (
                  <img
                    className="Role__Header__CompanyLogo"
                    src={role.company.logoUrl}
                    alt={t('alt.companyLogo', {
                      companyName: role.company.name,
                    })}
                  />
                ) : null}
                <h1 className="Role__Header__Name">{role.name}</h1>
                <p className="Role__Header__CompanyName">
                  {role.company.name}
                </p>
              </div>
              <div
                className="Role__Stages"
                aria-label={t('roles.listOfYourStages')}
              >
                {role.stages.map(stage => (
                  <RoleStageCard
                    key={stage.id}
                    roleId={role.id}
                    stageId={stage.id}
                    kind={stage.kind}
                    name={stage.name}
                    stageStatus={stage.stageStatus}
                    candidateStatus={stage.candidateStatus}
                    endTime={stage.endTime ? new Date(stage.endTime) : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </>
  );
};

export default Role;
